import type {
  BlockContent, CategoryProductLink, MessageButton, PaymentAccountData, PaymentAccountItem,
  PaymentAccountStatus,
} from '../../../../types/chat-message';

type UnknownRecord = Record<string, unknown>;

const isRecord = (value: unknown): value is UnknownRecord => (
  typeof value === 'object' && value !== null && !Array.isArray(value)
);

const cleanString = (value: unknown): string | undefined => {
  if (value === null || value === undefined) return undefined;
  return String(value).trim() || undefined;
};

const payloadOf = (block: BlockContent): UnknownRecord => (
  isRecord(block.payload) ? block.payload : {}
);

const statusOf = (value: unknown): PaymentAccountStatus | undefined => {
  if (value === true) return 'SUCCESS';
  if (value === false) return 'FAIL';
  const status = cleanString(value)?.toUpperCase();
  if (status === 'SUCCESS') return 'SUCCESS';
  if (status === 'FAIL') return 'FAIL';
  if (status === 'INVALID_INFO') return 'INVALID_INFO';
  return undefined;
};

const parseItem = (value: unknown): PaymentAccountItem | null => {
  if (!isRecord(value)) return null;
  const bankName = cleanString(value.bankName);
  const accountNumber = cleanString(value.accountNumber);
  const accountName = cleanString(value.accountName);
  if (!bankName || !accountNumber || !accountName) return null;
  return buildItem(value, bankName, accountNumber, accountName);
};

const buildItem = (value: UnknownRecord, bankName: string, accountNumber: string, accountName: string): PaymentAccountItem => ({
  id: cleanString(value.id), bankCode: cleanString(value.bankCode), bankName, accountNumber, accountName,
  iconUrl: cleanString(value.icon), accountType: cleanString(value.accountType),
  isValid: typeof value.isValid === 'boolean' ? value.isValid : true,
  status: statusOf(value.status), errorMessage: cleanString(value.errorMessage),
});

const normalizeUrl = (value: unknown): string => {
  const url = cleanString(value) || '';
  const markdownMatch = url.match(/^\[[^\]]*\]\((.*?)\)$/);
  return (markdownMatch ? markdownMatch[1] : url).trim();
};

const parseLink = (block: BlockContent): CategoryProductLink | null => {
  const payload = payloadOf(block);
  const text = cleanString(payload.text) || '';
  const url = normalizeUrl(payload.url);
  return text || url ? { text, url } : null;
};

const parseButtons = (payload: UnknownRecord): MessageButton[] => (
  Array.isArray(payload.buttons)
    ? payload.buttons.filter(isRecord).filter((item) => cleanString(item.name)) as unknown as MessageButton[]
    : []
);

const orderOf = (block: BlockContent): number => {
  const order = payloadOf(block).order;
  return typeof order === 'number' ? order : 0;
};

const collectLinks = (blocks: BlockContent[]): CategoryProductLink[] => blocks
  .filter((block) => block.type.toUpperCase() === 'LINK')
  .map(parseLink).filter((link): link is CategoryProductLink => !!link);

export const parsePaymentAccountBlocks = (
  intent: string | undefined, blockContents: BlockContent[] | undefined,
): PaymentAccountData | null => {
  if (intent?.trim().toLowerCase() !== 'add_payment_account' || !blockContents?.length) return null;
  const blocks = [...blockContents].sort((a, b) => orderOf(a) - orderOf(b));
  const listBlock = blocks.find((block) => block.type.toUpperCase() === 'PAYMENT_ACCOUNT_LIST');
  const payload = listBlock ? payloadOf(listBlock) : {};
  if (!Array.isArray(payload.data)) return null;
  return createData(blocks, payload, payload.data.map(parseItem).filter((item): item is PaymentAccountItem => !!item));
};

const createData = (blocks: BlockContent[], payload: UnknownRecord, items: PaymentAccountItem[]): PaymentAccountData => ({
  title: blocks.filter((block) => block.type.toUpperCase() === 'TEXT')
    .map((block) => cleanString(payloadOf(block).text)).find(Boolean) || '',
  items, buttons: parseButtons(payload), links: collectLinks(blocks),
  isResult: items.some((item) => item.status !== undefined),
});

export const paymentAccountWarning = (item: PaymentAccountItem): string | undefined => (
  item?.errorMessage || undefined
);

export const isPaymentAccountButtonHidden = (button: MessageButton): boolean => (
  button.commandVariables?.some((item) => item.name === 'hidden_message' && item.value === 'true') || false
);

export const formatPaymentAccountCopy = (data: PaymentAccountData): string => {
  const items = (data?.items || []).map((item) => [item?.bankName, item?.accountNumber, item?.accountName, item ? paymentAccountWarning(item) : undefined]
    .filter(Boolean).join('\n'));
  return [data?.title, ...items, ...(data?.links || []).map((link) => link?.text || link?.url)].filter(Boolean).join('\n\n');
};
